import { DashboardState, IConfig } from "@kuika/kuika-cl-model";
import { IProjectsResponseDto } from "@kuika/kuika-cl-model/src/dtos/projects-response";
import classNames from "classnames";
import * as React from "react";
import { connect } from "react-redux";
import { ProjectService } from "../../../../services/project-service";
import { ScreenService } from "../../../../services/screen-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithHistoryProp, withHistory } from "../../../../shared/hoc/with-history";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { getProjectEntryScreenId } from "../../../../shared/utility/project-entry-screen";
import { AppState } from "../../../../store/root-reducer";
import { setNextCallbackFunction } from "../../../../store/unsaved-changes-modal/actions";
import { ProjectAppIcon } from "../../../management/projects/project-app-icon/project-app-icon";
import KuikaDropdownMenu, { KuikaDropdownMenuItemType } from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import "./kuika-logo.scss";

export interface ILogoProps {
  activeGlobalConfiguration?: IConfig;
  setNextCallbackFunction: (cb?: () => void) => void;
}

interface IKuikaLogoState {
  recentApps: IProjectsResponseDto[];
  isMenuOpen: boolean;
  searchText: string;
  /** False until the first answer (or failure) — the panel says "Loading" rather than "No app found". */
  hasLoadedRecentApps: boolean;
}

const APPS_ROUTE_SEGMENT: string = "projects";

/** Long enough to stay clear of the designer's own load burst, short enough to beat a first click. */
const RECENT_APPS_PREFETCH_DELAY_MS: number = 3000;

/**
 * Every management route is `/{workspace}/{tool}`, so the Apps screen family — `/projects` plus
 * its six sub-views (recent, myprojects, favorites, allprojects, archive, templates) — is exactly
 * the paths whose second segment is `projects`. Reading the segment instead of comparing against
 * `window.currentDomain` keeps the check correct before that global is assigned.
 */
const isAppsScreenPath = (pathname: string): boolean => {
  const segments: string[] = pathname.split("/").filter((segment: string): boolean => segment.length > 0);
  return segments[1] === APPS_ROUTE_SEGMENT;
};

/** The menu lists this many recent apps; a search looks through all of them. */
const RECENT_APPS_SHOWN: number = 5;

/**
 * Case- and accent-blind, so "sirket" finds "Şirket" — NFD strips the combining marks, and the
 * dotless ı is mapped by hand because NFD leaves it be. Same folding as the designer's switchers.
 */
const normalizeForMatch = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ı/g, "i")
    .toLowerCase();

/**
 * The brand mark, in both shell headers, and the app menu it opens: "Back to Apps" on top, a
 * search, and the user's five most recent apps — so moving between apps no longer means a
 * detour through the Apps page. The account menu stays on the avatar
 * (UserMenuButton); there is exactly one account menu per shell.
 *
 * On the Apps screen itself the mark is inert: the page already lists every app, its only
 * destination is the screen you are already on, and pointer and hover feedback would promise
 * otherwise.
 *
 * Opening an app from here remounts the designer (see `screen-design-route.tsx`); an open editor
 * with unsaved work gets its confirmation first, exactly as the old "back to Apps" click did.
 */
class KuikaLogo extends React.Component<ILogoProps & IWithHistoryProp & IWithSDProps, IKuikaLogoState> {
  state: IKuikaLogoState = { recentApps: [], isMenuOpen: false, searchText: "", hasLoadedRecentApps: false };

  private isUnmounted: boolean = false;

  private hasPrefetched: boolean = false;

  private prefetchTimer: number | undefined;

  componentDidMount(): void {
    // The recent-apps query takes seconds on a large workspace, so it is started once the page has
    // settled rather than on the click — the panel then usually opens already filled.
    if (!isAppsScreenPath(this.props.navigator.location.pathname)) {
      this.prefetchTimer = window.setTimeout(this.prefetchRecentApps, RECENT_APPS_PREFETCH_DELAY_MS);
    }
  }

  componentWillUnmount(): void {
    this.isUnmounted = true;
    window.clearTimeout(this.prefetchTimer);
  }

  loadRecentApps = async (): Promise<void> => {
    const projects: IProjectsResponseDto[] | null = await ProjectService.GetRecentProjectsQuietly();
    if (this.isUnmounted) return;
    // A failed refresh keeps the list already shown; only the first answer ends "Loading".
    if (!projects) {
      this.setState({ hasLoadedRecentApps: true });
      return;
    }
    this.setState({ recentApps: projects, hasLoadedRecentApps: true });
  };

  /** Once per mount — after the page settles, or on the first hover or focus if that comes sooner. */
  prefetchRecentApps = (): void => {
    if (this.hasPrefetched) return;
    this.hasPrefetched = true;
    window.clearTimeout(this.prefetchTimer);
    void this.loadRecentApps();
  };

  handleOpenChange = (open: boolean): void => {
    this.setState({ isMenuOpen: open, searchText: open ? this.state.searchText : "" });
    // Refreshed on every open: the order is "last opened", and it changes with every switch.
    if (open) void this.loadRecentApps();
  };

  hasOpenEditor = (): boolean => {
    const { sd, activeGlobalConfiguration } = this.props;
    return Boolean(
      sd?.activeConfiguration ||
      sd?.activeEditingDatasource ||
      sd?.activeCreatingDatasourceType !== undefined ||
      sd?.creatingActionsDatasource ||
      sd?.editingTable ||
      sd?.editingAction ||
      sd?.activeCreatingTableName ||
      sd?.activeCreatingActionName ||
      sd?.editingNotificationTemplate ||
      sd?.getSelectedApi() ||
      activeGlobalConfiguration ||
      sd.dashboardState === DashboardState.runSettings
    );
  };

  /**
   * Leaves the open app — for the Apps page or for another app. The open editors are closed on
   * the way out, and when one of them may hold unsaved work its confirmation runs first.
   */
  leaveTo = (navigate: () => void): void => {
    const { sd } = this.props;
    const leave = (): void => {
      navigate();

      sd.setCreateNotificationTemplateActive();
      sd.clearNotificationTemplatePanel();
      sd.cancelEditingScheduledJob();
      sd.cancelEditingApi();
      sd.cancelActiveMethod();
      sd.cancelCreatingOrEditingDatasource();
      sd.cancelCreatingOrEditingConfiguration();
      sd.clearSelectedWf();
    };

    if (this.hasOpenEditor()) {
      this.props.setNextCallbackFunction(leave);
      return;
    }

    leave();
  };

  openApp = (project: IProjectsResponseDto): void => {
    this.handleOpenChange(false);
    if (project.id === this.props.sd?.project?.id) return;
    const screenId: string = getProjectEntryScreenId(project);
    // The list already says which app this screen belongs to; telling the lookup spares the
    // designer route a round trip before it swaps the designer over.
    ScreenService.rememberProjectOfScreen(screenId, project.id);
    this.leaveTo((): void => this.props.navigator.gotoScreen(screenId));
  };

  openAppsPage = (): void => {
    this.handleOpenChange(false);
    this.leaveTo((): void => this.props.navigator.gotoProjects());
  };

  /** The first five recent apps; while searching, every recent app whose name matches. */
  getListedApps = (): IProjectsResponseDto[] => {
    const needle: string = normalizeForMatch(this.state.searchText.trim());
    if (!needle) return this.state.recentApps.slice(0, RECENT_APPS_SHOWN);
    return this.state.recentApps.filter((project: IProjectsResponseDto): boolean =>
      normalizeForMatch(project.name ?? "").includes(needle)
    );
  };

  // The open app is marked the way every picker menu in the designer marks its value (the
  // screen Role menu, the workspace switcher): the menu's own selected row — highlight and a
  // leading check — driven by `selected`, not a check drawn by this component.
  getMenuOptions = (): KuikaDropdownMenuItemType[] =>
    this.getListedApps().map((project: IProjectsResponseDto): KuikaDropdownMenuItemType => ({
      key: project.id,
      label: project.name,
      group: "Recent",
      icon: <ProjectAppIcon project={project} size={20} />,
      showSelectedIcon: true,
      dataTestId: `headerAppSwitcherItem_${project.id}`,
      onClick: (): void => this.openApp(project)
    }));

  renderMenuHeader = (): JSX.Element => (
    <div className="kuika-logo__menu-header">
      <button
        type="button"
        className="kuika-logo__back"
        data-testid="headerAppMenuBackToApps"
        onClick={this.openAppsPage}
      >
        <KuikaSvg name="arrow-left" type="arrows" width={16} height={16} />
        <span>Back to Apps</span>
      </button>
      <KuikaInput
        data-testid="headerAppSwitcherSearch"
        size="sm"
        autoFocus
        placeholder="Search apps..."
        value={this.state.searchText}
        onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
          this.setState({ searchText: event.target.value })
        }
        leftIcon={<KuikaSvg name="search" width={14} height={14} />}
      />
    </div>
  );

  renderEmptyState = (): JSX.Element => (
    <div className="kuika-logo__menu-empty">{this.state.hasLoadedRecentApps ? "No app found" : "Loading apps..."}</div>
  );

  renderLogoMark = (): JSX.Element => {
    if (KMainFunctions.isMainAI()) {
      return <img src="/assets/main-ai/favicon_32x32.svg" />;
    }
    // Fills are left to kuika-logo.scss (badge = --blue-5, mark = --white) so the brand colours
    // come from tokens instead of hardcoded hex.
    return (
      <svg width="32" height="32" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0 10C0 5.28597 0 2.92894 1.46447 1.46447C2.92894 1.58946e-07 5.28595 0 10 0C14.714 0 17.0711 1.58946e-07 18.5355 1.46447C20 2.92894 20 5.28597 20 10C20 14.7141 20 17.0711 18.5355 18.5356C17.0711 20 14.714 20 10 20C5.28595 20 2.92894 20 1.46447 18.5356C0 17.0711 0 14.7141 0 10Z" />
        <path d="M9.21177 15.766C8.86706 15.766 8.48913 15.6523 8.08629 15.4334C6.22987 14.4187 4.85105 12.8736 4.17826 11.7663C3.80448 11.1516 3.58852 10.6885 3.57606 10.137V10.0864C3.57606 10.057 3.57191 10.0233 3.57606 9.99381C3.57606 9.95171 3.57606 9.91803 3.57606 9.88856V9.83803C3.59268 9.28229 3.80864 8.81916 4.18241 8.20868C4.85521 7.1014 6.23402 5.55625 8.09044 4.54159C8.49329 4.32266 8.87122 4.20898 9.21592 4.20898C9.56062 4.20898 9.78489 4.31845 9.91779 4.3816L9.93855 4.39002C10.358 4.5879 10.6404 4.86999 10.7733 5.23628L10.7858 5.26575C10.8315 5.37942 10.9436 5.64888 10.7775 6.04885C10.6363 6.38566 10.2293 6.76879 10.0133 6.97088C9.96347 7.01719 9.92194 7.05509 9.89702 7.08456C9.12455 7.87187 8.16104 8.85284 8.1735 9.9896C8.15689 11.1221 9.1204 12.1031 9.89702 12.8904C9.92609 12.9199 9.96347 12.9578 10.0133 13.0041C10.2334 13.2104 10.6363 13.5893 10.7775 13.9261C10.9436 14.3261 10.8356 14.5956 10.7858 14.7092L10.7733 14.7387C10.6363 15.1008 10.358 15.3871 9.93855 15.585L9.91779 15.5934C9.78074 15.6565 9.55232 15.766 9.21592 15.766H9.21177Z" />
        <path d="M12.7589 13.731C11.3718 13.4405 10.1508 12.1522 9.70639 11.3944C8.82594 9.8871 9.66901 8.61562 9.70639 8.5651C9.723 8.53984 11.3967 6.14844 12.8669 6.14844C12.9084 6.14844 12.9499 6.14844 12.9873 6.15686C14.2872 6.32527 15.5165 7.68516 16.0273 8.55668C16.9119 10.0639 16.0647 11.3438 16.0273 11.3986C16.0107 11.4196 14.3495 13.7226 12.8793 13.7394C12.8793 13.7394 12.8046 13.7394 12.7547 13.731H12.7589Z" />
      </svg>
    );
  };

  render() {
    const isOnAppsScreen: boolean = isAppsScreenPath(this.props.navigator.location.pathname);

    if (isOnAppsScreen) {
      return (
        <div className="kuika-logo">
          <div data-testid="headerLogoApps" data-onboarding-id="header_logo_apps" className="kuika-logo__mark">
            {this.renderLogoMark()}
          </div>
        </div>
      );
    }

    const options: KuikaDropdownMenuItemType[] = this.getMenuOptions();

    return (
      <div
        className="kuika-logo"
        data-onboarding-id="header_logo_apps"
        onPointerEnter={this.prefetchRecentApps}
        onFocus={this.prefetchRecentApps}
      >
        <KuikaDropdownMenu
          options={options}
          open={this.state.isMenuOpen}
          setOpen={this.handleOpenChange}
          selected={this.props.sd?.project?.id ?? null}
          placement="bottomLeft"
          overlayClassName="kuika-logo__menu"
          menuTestId="headerAppSwitcher"
          header={this.renderMenuHeader()}
          emptyState={this.renderEmptyState()}
          emptyStateCondition={options.length === 0}
        >
          {/* The mark never leaves: on hover, focus and while the menu is open it settles onto a
              hover surface and a caret badge joins its corner, so it reads as a control that opens
              something while staying the brand mark. */}
          <button
            type="button"
            data-testid="headerLogoApps"
            aria-label="Switch app"
            aria-haspopup="menu"
            aria-expanded={this.state.isMenuOpen}
            className={classNames("kuika-logo__trigger", {
              "kuika-logo__trigger--open": this.state.isMenuOpen
            })}
          >
            <span className="kuika-logo__mark">{this.renderLogoMark()}</span>
            <span className="kuika-logo__caret" aria-hidden="true">
              <KuikaSvg name="chevron-down" type="arrows" width={10} height={10} />
            </span>
          </button>
        </KuikaDropdownMenu>
      </div>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  activeGlobalConfiguration: state.globalConfig.activeGlobalConfiguration
});

const mapDispatchToProps = (dispatch) => ({
  setNextCallbackFunction: (cb?: () => void) => dispatch(setNextCallbackFunction(cb))
});

const kuikaLogo = connect(mapStateToProps, mapDispatchToProps)(withHistory<ILogoProps>(withSD<ILogoProps>(KuikaLogo)));
export { kuikaLogo as KuikaLogo };
